import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Layout } from '@/components/layout'
import { useQueueStatus, useSystemHealth, useSystemInfo, useSystemMetrics } from '@/api/system'
import { useAuthStore } from '@/lib/stores/authStore'
import { useToast } from '@/lib/hooks/useToast'
import { contactsService } from '@/lib/services/contactsService'
import {
  RefreshCw,
  Activity,
  ArrowRight,
  UserCog,
  FileText,
  Heart,
  Server,
  BarChart3,
  Shield,
  Plug,
  Trash2,
} from 'lucide-react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

export function AdminOverviewPage() {
  const { t } = useTranslation()
  const { toast } = useToast()
  const { data: queueData, refetch: refetchQueues } = useQueueStatus()
  const { data: healthData } = useSystemHealth()
  const { data: infoData } = useSystemInfo()
  const { data: metricsData } = useSystemMetrics()
  const { hasPermission } = useAuthStore()
  const [showCleanupDialog, setShowCleanupDialog] = useState(false)
  const [isCleaningUp, setIsCleaningUp] = useState(false)
  const [cleanupResult, setCleanupResult] = useState<{
    contactgegevens_deleted: number
    piketnummers_deleted: number
    total_deleted: number
  } | null>(null)

  const handleRefresh = () => {
    refetchQueues()
  }

  const handleCleanupContacts = async () => {
    setIsCleaningUp(true)
    try {
      const result = await contactsService.cleanupOrphanedContacts()
      setCleanupResult(result)
      toast({
        title: 'Contacten opgeschoond',
        description: `${result.contactgegevens_deleted} contactgegevens en ${result.piketnummers_deleted} piketnummers zijn verwijderd.`,
        variant: 'default',
      })
    } catch (error) {
      console.error('Failed to cleanup contacts:', error)
      toast({
        title: 'Fout bij opschonen',
        description: 'Er is een fout opgetreden bij het opschonen van contacten.',
        variant: 'destructive',
      })
    } finally {
      setIsCleaningUp(false)
    }
  }

  return (
    <>
      <Layout>
        {/* Header */}
        <div className="border-b bg-muted/30">
          <div className="container mx-auto px-6 py-6">
            <div className="flex-column md:flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold tracking-tight text-foreground">
                  {t('admin.dashboard')}
                </h1>
                <p className="text-muted-foreground mt-2">{t('admin.beschrijving')}</p>
              </div>
              <div className="flex gap-2 mt-4 md:mt-0">
                <Button onClick={handleRefresh} variant="secondary" size="sm">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  {t('admin.vernieuwen')}
                </Button>
                {hasPermission('contacts.cleanup') && (
                  <Button
                    onClick={() => setShowCleanupDialog(true)}
                    variant="destructive"
                    size="sm"
                    disabled={isCleaningUp}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Contacten opschonen
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="container mx-auto px-6 py-8 space-y-6">
          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {hasPermission('system.settings.view') && (
            <Card
              className="hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => (window.location.href = '/admin/workers')}
            >
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center space-x-2">
                    <Activity className="h-5 w-5" />
                    <span>{t('admin.werkers')}</span>
                  </span>
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  {t('admin.werkersamenvatting')} en statistieken
                </p>
              </CardContent>
            </Card>
          )}

          {hasPermission('users.list') && (
            <Card
              className="hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => (window.location.href = '/admin/users')}
            >
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center space-x-2">
                    <UserCog className="h-5 w-5" />
                    <span>{t('admin.users.title')}</span>
                  </span>
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">{t('admin.users.description')}</p>
              </CardContent>
            </Card>
          )}

          {hasPermission('rbac.roles.view') && (
            <Card
              className="hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => (window.location.href = '/admin/rbac/roles')}
            >
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center space-x-2">
                    <Shield className="h-5 w-5" />
                    <span>{t('rbac.rolesAndPermissions')}</span>
                  </span>
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">{t('rbac.manageRolesDescription')}</p>
              </CardContent>
            </Card>
          )}

          {hasPermission('integration.list') && (
            <Card
              className="hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => (window.location.href = '/admin/integrations')}
            >
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center space-x-2">
                    <Plug className="h-5 w-5" />
                    <span>{t('integration.title', 'Integrations')}</span>
                  </span>
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  {t('integration.description', 'Configure and manage external service integrations (SharePoint, Coosto)')}
                </p>
              </CardContent>
            </Card>
          )}

          {hasPermission('audit.list') && (
            <Card
              className="hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => (window.location.href = '/admin/audit-logs')}
            >
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center space-x-2">
                    <FileText className="h-5 w-5" />
                    <span>{t('admin.audit.title')}</span>
                  </span>
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">{t('admin.audit.description')}</p>
              </CardContent>
            </Card>
          )}

          {hasPermission('practice.list') && (
            <Card
              className="hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => (window.location.href = '/admin/practice-cases')}
            >
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center space-x-2">
                    <FileText className="h-5 w-5" />
                    <span>Oefenscenario's</span>
                  </span>
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Beheer oefenscenario's voor trainingsoefeningen
                </p>
              </CardContent>
            </Card>
          )}
        </div>


        {/* System Health & Stats */}
        {hasPermission('system.settings.view') && (
          <div>
            <h2 className="text-xl font-semibold mb-4">{t('admin.systeemgezondheid')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* System Health Card */}
              <Card className="animate-fade-in">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{t('admin.systeemstatus')}</CardTitle>
                <Heart className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm text-muted-foreground">{t('admin.status')}:</span>
                    <Badge variant={healthData?.status === 'healthy' ? 'default' : 'destructive'}>
                      {healthData?.status || 'Unknown'}
                    </Badge>
                  </div>
                  {healthData?.services &&
                    Object.entries(healthData.services).map(([serviceName, serviceData]) => (
                      <div
                        key={serviceName}
                        className="flex items-center justify-between text-xs border-t pt-2"
                      >
                        <div className="flex flex-col">
                          <span className="font-medium capitalize">{serviceName}</span>
                          {serviceData.message && (
                            <span className="text-muted-foreground text-[10px]">
                              {serviceData.message}
                            </span>
                          )}
                        </div>
                        <Badge
                          variant={serviceData.status === 'healthy' ? 'default' : 'destructive'}
                          className="text-[10px] px-1.5 py-0"
                        >
                          {serviceData.status}
                        </Badge>
                      </div>
                    ))}
                </div>
              </CardContent>
            </Card>

            {/* System Info Card */}
            <Card className="animate-fade-in">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  {t('admin.systeeminformatie')}
                </CardTitle>
                <Server className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{t('admin.apiversie')}:</span>
                    <span>{infoData?.apiVersion || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{t('admin.pythonversie')}:</span>
                    <span>{infoData?.pythonVersion || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between text-sm items-start">
                    <span className="text-muted-foreground">{t('admin.platform')}:</span>
                    <span className="max-w-[18rem] text-right break-words whitespace-normal">
                      {infoData?.platform || 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{t('admin.uptime')}:</span>
                    <span>{infoData?.uptime || 'N/A'}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* System Metrics Card */}
            <Card className="animate-fade-in">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{t('admin.apimetrics')}</CardTitle>
                <BarChart3 className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{t('admin.totaalverzoeken')}:</span>
                    <span>{metricsData?.requestsTotal?.toLocaleString() || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{t('admin.verzoekenperminuut')}:</span>
                    <span>{metricsData?.requestsPerMinute || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{t('admin.foutpercentage')}:</span>
                    <span
                      className={
                        metricsData?.errorRate && metricsData.errorRate > 5 ? 'text-red-600' : ''
                      }
                    >
                      {metricsData?.errorRate ? `${metricsData.errorRate.toFixed(2)}%` : 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{t('admin.gemantwoordtijd')}:</span>
                    <span>
                      {metricsData?.responseTimeAvg
                        ? `${metricsData.responseTimeAvg.toFixed(2)}ms`
                        : 'N/A'}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
            </div>
          </div>
        )}

        {/* Queue Status Cards */}
        {hasPermission('system.settings.view') && (
          <div>
            <h2 className="text-xl font-semibold mb-4">{t('admin.taakwachtrijen')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {queueData?.queues &&
                Object.entries(queueData.queues).map(([queueName, stats]) => (
                  <Card key={queueName} className="animate-fade-in">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">
                        {t('admin.wachtrij')}: {queueName}
                      </CardTitle>
                      <Activity className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">{t('admin.inWachtrij')}:</span>
                          <Badge variant="secondary">{stats.queued}</Badge>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">{t('admin.gestart')}:</span>
                          <Badge variant="secondary">{stats.started}</Badge>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">{t('admin.voltooid')}:</span>
                          <Badge variant="secondary">{stats.finished}</Badge>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">{t('admin.mislukt')}:</span>
                          <Badge variant="destructive">{stats.failed}</Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
            </div>
          </div>
        )}
      </div>
    </Layout>

    {/* Cleanup Dialog */}
    <AlertDialog open={showCleanupDialog} onOpenChange={setShowCleanupDialog}>
      <AlertDialogContent>
        <AlertDialogTitle>Contacten opschonen</AlertDialogTitle>
        <AlertDialogDescription>
          {cleanupResult ? (
            <div className="space-y-2">
              <p className="font-semibold">Opschonen voltooid:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Contactgegevens verwijderd: {cleanupResult.contactgegevens_deleted}</li>
                <li>Piketnummers verwijderd: {cleanupResult.piketnummers_deleted}</li>
                <li className="font-semibold">
                  Totaal verwijderd: {cleanupResult.total_deleted}
                </li>
              </ul>
            </div>
          ) : (
            'Dit zal alle contactgegevens verwijderen die niet gekoppeld zijn aan actieve crises. Dit kan niet ongedaan gemaakt worden.'
          )}
        </AlertDialogDescription>
        <div className="flex gap-2 justify-end">
          {cleanupResult ? (
            <Button onClick={() => setShowCleanupDialog(false)}>Sluiten</Button>
          ) : (
            <>
              <AlertDialogCancel>Annuleren</AlertDialogCancel>
              <AlertDialogAction onClick={handleCleanupContacts} disabled={isCleaningUp}>
                {isCleaningUp ? 'Bezig...' : 'Opschonen'}
              </AlertDialogAction>
            </>
          )}
        </div>
      </AlertDialogContent>
    </AlertDialog>
  </>
  )
}
